import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useConfig } from '../context/ConfigContext';

const GalleryPage = () => {
    const { slug } = useParams();
    const { config } = useConfig();
    const [pageData, setPageData] = useState(null);
    const [selectedImage, setSelectedImage] = useState(null);

    useEffect(() => {
        if (config?.customGalleryPages) {
            const page = config.customGalleryPages.find(p => p.slug === slug);
            setPageData(page);
        }
    }, [config, slug]);

    if (!pageData) {
        return (
            <div className="flex flex-col items-center justify-center py-40">
                <div className="w-12 h-12 border-4 border-white/10 border-t-purple-600 rounded-full animate-spin"></div>
                <p className="text-white/20 text-[10px] font-black uppercase tracking-widest mt-6">Sincronizando Galería</p>
            </div>
        );
    }

    // Embed Mode: Occupies full width and high height
    if (pageData.type === 'embed') {
        return (
            <div className="w-full h-screen bg-black animate-fadeIn overflow-hidden relative z-0" style={{ WebkitOverflowScrolling: 'touch', overflowY: 'auto' }}>
                <iframe 
                    src={pageData.url} 
                    className="w-full h-full border-none"
                    title={pageData.title}
                    allowFullScreen
                    scrolling="yes"
                />
            </div>
        );
    }

    // Default Photo Upload Mode
    return (
        <div className="animate-fadeIn pb-20 px-4">
            <header className="mb-12 text-center">
                <div className="inline-block p-6 bg-white/5 backdrop-blur-xl rounded-[35px] border border-white/10 text-4xl mb-6 shadow-2xl">
                    {pageData.icon || '🖼️'}
                </div>
                <h1 className="text-4xl sm:text-5xl font-black text-white tracking-tighter italic uppercase">{pageData.title}</h1>
                <p className="text-white/40 font-bold mt-3 uppercase tracking-widest text-[10px]">Galería de momentos NeuroUpdate</p>
            </header>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
                {(pageData.images || []).map((img, index) => (
                    <div 
                        key={index}
                        onClick={() => setSelectedImage(img)}
                        className="group relative cursor-pointer overflow-hidden rounded-[40px] border border-white/5 bg-white/5 backdrop-blur-md shadow-xl hover:shadow-purple-500/20 transition-all hover:scale-[1.02] active:scale-95 duration-500"
                    >
                        <div className="aspect-[4/3] overflow-hidden">
                            <img 
                                src={img.url} 
                                alt={img.caption || 'Imagen'} 
                                className="w-full h-full object-cover transform transition-transform duration-1000 group-hover:scale-110"
                                loading="lazy"
                            />
                        </div>
                        {img.caption && (
                            <div className="p-6 bg-gradient-to-b from-transparent to-black/40">
                                <p className="text-white font-black leading-tight uppercase tracking-tight text-xs">
                                    {img.caption}
                                </p>
                            </div>
                        )}
                        <div className="absolute inset-0 bg-gradient-to-t from-purple-900/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end justify-center p-8">
                            <span className="text-white text-[10px] font-black uppercase tracking-[0.3em] bg-purple-600 px-6 py-2 rounded-full shadow-lg transform translate-y-4 group-hover:translate-y-0 transition-transform duration-500">
                                Ampliar
                            </span>
                        </div>
                    </div>
                ))}
            </div>

            {(!pageData.images || pageData.images.length === 0) && (
                <div className="text-center py-32 bg-white/5 backdrop-blur-sm rounded-[50px] border-2 border-dashed border-white/10 p-10 animate-pulse">
                    <span className="text-6xl grayscale opacity-10">📸</span>
                    <p className="text-white/20 font-black mt-6 text-[10px] uppercase tracking-[0.4em]">Sin contenido multimedia disponible</p>
                </div>
            )}

            {/* Premium Lightbox */}
            {selectedImage && (
                <div 
                    className="fixed inset-0 z-[100] bg-[#0c001e]/98 backdrop-blur-2xl flex items-center justify-center p-4 sm:p-10 animate-fadeIn"
                    onClick={() => setSelectedImage(null)}
                >
                    <button 
                        className="absolute top-8 right-8 w-12 h-12 bg-white/5 hover:bg-white/10 text-white rounded-full flex items-center justify-center text-2xl transition-all hover:rotate-90"
                        onClick={() => setSelectedImage(null)}
                    >
                        ✕
                    </button>
                    <div className="max-w-6xl w-full flex flex-col items-center">
                        <div className="relative rounded-[40px] overflow-hidden shadow-[0_0_80px_rgba(139,92,246,0.3)] border-4 border-white/5 animate-scaleIn">
                            <img 
                                src={selectedImage.url} 
                                alt="Preview" 
                                className="max-h-[85vh] w-auto object-contain"
                            />
                        </div>
                        {selectedImage.caption && (
                            <div className="mt-8 bg-purple-600 px-10 py-4 rounded-full shadow-2xl border border-white/20">
                                <p className="text-white font-black text-center uppercase tracking-[0.2em] text-xs">
                                    {selectedImage.caption}
                                </p>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default GalleryPage;
